import { useEffect, useRef } from "react";
import logoMarkSrc from "../assets/oceanum-logo-mark.svg";

const FRAME_COUNT = 24;
const FRAME_INTERVAL = 80; // ms per frame
const FAVICON_SIZE = 32;

/**
 * Pre-renders rotated frames of the Oceanum logo mark and cycles them
 * as the favicon when `spinning` is true. Restores the static favicon
 * when `spinning` becomes false.
 */
export function useSpinningFavicon(spinning: boolean) {
  const framesRef = useRef<string[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const frameIdx = useRef(0);
  const staticHref = useRef<string>("/favicon.png");

  // Pre-render rotated frames once
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const frames: string[] = [];
      const canvas = document.createElement("canvas");
      canvas.width = FAVICON_SIZE;
      canvas.height = FAVICON_SIZE;
      const ctx = canvas.getContext("2d")!;

      for (let i = 0; i < FRAME_COUNT; i++) {
        ctx.clearRect(0, 0, FAVICON_SIZE, FAVICON_SIZE);
        // Dark circular background so logo is visible on light browser themes
        ctx.beginPath();
        ctx.arc(FAVICON_SIZE / 2, FAVICON_SIZE / 2, FAVICON_SIZE / 2, 0, 2 * Math.PI);
        ctx.fillStyle = "#0f172a"; // slate-900
        ctx.fill();
        ctx.save();
        ctx.translate(FAVICON_SIZE / 2, FAVICON_SIZE / 2);
        ctx.rotate((2 * Math.PI * i) / FRAME_COUNT);
        ctx.drawImage(
          img,
          -FAVICON_SIZE / 2,
          -FAVICON_SIZE / 2,
          FAVICON_SIZE,
          FAVICON_SIZE
        );
        ctx.restore();
        frames.push(canvas.toDataURL("image/png"));
      }
      framesRef.current = frames;

      // Also generate the static favicon (frame 0 = no rotation)
      staticHref.current = frames[0];
      // Set it immediately if not spinning
      const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (link && !timerRef.current) link.href = frames[0];
    };
    img.src = logoMarkSrc;
  }, []);

  // Start/stop animation
  useEffect(() => {
    const link =
      document.querySelector<HTMLLinkElement>('link[rel="icon"]') ??
      (() => {
        const el = document.createElement("link");
        el.rel = "icon";
        document.head.appendChild(el);
        return el;
      })();

    if (spinning && framesRef.current.length > 0) {
      frameIdx.current = 0;
      timerRef.current = setInterval(() => {
        link.href = framesRef.current[frameIdx.current % FRAME_COUNT];
        frameIdx.current++;
      }, FRAME_INTERVAL);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      link.href = staticHref.current;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [spinning]);
}
